import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { Grid, Typography } from '@mui/material';
import { FastField, Field } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import { ILenderOptionListItem } from '@modules/admin/Tools/shared/LenderControlsHeader';
import { getOptions, queryParams, withQueryParams } from '@shared/commonFunctions';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { SERVICES } from '@shared/constants/apiConfig';
import { Roles } from '@shared/constants/constants';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { ISelectFieldProps, SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { useTabs } from '@shared/hooks/useTabs';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { setDealReCalculate } from '@store/reducers/contactManagerSlice';
import { LenderDropDown } from '@styles/ContactManager/DealStyles';
import { StyledNumberInputWrapper, StyledSelectWrapper } from '@styles/InputStyles';
import { muiTheme } from '@styles/muiTheme';
import { LenderWrapper } from '@styles/StyledDiv';

import { setIsDealStructureChanged } from '@store/reducers/dealSlice';
import { ILenderSelctorProps } from '../interfaces';

export const LenderSelector = ({ dealResponse, setFieldValue, setLoading }: ILenderSelctorProps) => {
  const { isAdmin, userHasRoles } = useUserAccount();
  const { openTab } = useTabs();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const skipNextBlur = useRef(false);
  const hasEditCudlRatePermission = userHasRoles([Roles.EDIT_CUDL_APPROVAL_FIELDS]);
  const dealId = queryParams()?.dealId;
  const { data: lenderUtility } = useSWR(
    dealResponse?.lenderResponse?.lenderId && `${SERVICES.LENDER}/lenders/${dealResponse?.lenderResponse?.lenderId}`
  );
  const isCudl = useMemo(
    () => !lenderUtility?.isDefi && !lenderUtility?.isDealerTrack && !lenderUtility?.isOpenLending && !lenderUtility?.isQuilo,
    [lenderUtility]
  );
  const canEditRateAndTerm = useMemo(
    () => (!isAdmin && isCudl ? hasEditCudlRatePermission : true),
    [hasEditCudlRatePermission, isAdmin, isCudl]
  );

  const lenderId = dealResponse?.lenderResponse?.lenderId;

  const {
    data: lendersList,
    mutate,
    isValidating,
    error: lendersError,
  } = useSWR<ILenderOptionListItem[]>(
    dealResponse?.id &&
      withQueryParams(
        `/${SERVICES.LENDER}/deals-tab-eligible-lenders`,
        {
          dealId: dealResponse?.id,
          leadId: dealResponse?.leadId,
          contactId: dealResponse?.contact?.id,
          lenderCode: dealResponse?.lenderResponse?.lenderCode,
          coBorrower: dealResponse?.coContact?.id,
          UseDealCoBorrower: true,
        },
        false
      ),
    {
      revalidateOnMount: false,
    }
  );

  const loading = useMemo(
    () => (dealResponse?.id && !lendersList && !lendersError) || isValidating,
    [lendersList, isValidating, lendersError, dealResponse?.id]
  );

  useEffect(() => {
    mutate();
  }, [dealResponse?.lenderResponse?.lenderCode]);

  const lenderOptions = useMemo(() => {
    const sortedOptions =
      lendersList?.sort((optionOne, optionTwo) => optionOne.name.toUpperCase().localeCompare(optionTwo.name.toUpperCase())) ?? [];

    return getOptions(sortedOptions, 'code');
  }, [lendersList]);

  const handleOnChange = useCallback(
    async (value: string) => {
      if (dealResponse) {
        setLoading(true);
        setFieldValue?.('lenderResponse.lenderCode', value as string);
        dispatch(setDealReCalculate(true));
        setLoading(false);
      }
    },
    [dealResponse]
  );

  const handleOnBlur = useCallback(() => {
    if (skipNextBlur.current) {
      skipNextBlur.current = false;
      return;
    }
    dispatch(setDealReCalculate(true));
  }, [dispatch]);

  const handleRateKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        skipNextBlur.current = true;
        dispatch(setDealReCalculate(true));
      }
    },
    [dispatch]
  );

  const handleOnTermChange = useCallback(() => {
    if (skipNextBlur.current) {
      skipNextBlur.current = false;
      return;
    }
    handleOnBlur();
    dealId && dispatch(setIsDealStructureChanged({ data: { isDealStructureChanged: true }, dealId }));
  }, [dispatch, dealId]);

  const handleTermKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        skipNextBlur.current = true;
        dispatch(setDealReCalculate(true));
        dealId && dispatch(setIsDealStructureChanged({ data: { isDealStructureChanged: true }, dealId }));
      }
    },
    [dispatch, dealId]
  );

  const handleLenderInfo = useCallback(
    (id: string) => {
      const lenderName = dealResponse?.lenderResponse?.lenderName ?? '';
      const route = withQueryParams('/lender-info', { lenderId: id }, false);
      const title = `${lenderName} - LenderInfo`;
      openTab({ title, route });
      navigate(route);
    },
    [dealResponse?.lenderResponse?.lenderName]
  );

  return (
    <>
      <Typography
        variant="h3"
        marginBottom="8px"
      >
        Lender
        <ExclamationCircleIcon
          width={24}
          color={muiTheme.palette.success.main}
          style={{ cursor: 'pointer' }}
          onClick={() => handleLenderInfo(lenderId || '')}
        />
      </Typography>

      <LenderWrapper className="lenderWrapper">
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            md={6}
          >
            <LenderDropDown>
              <LoaderBackdrop
                contained
                open={loading}
                size={20}
                zIndex={2}
              />
              <FastField
                key={lenderOptions}
                name="lenderResponse.lenderCode"
                label="Selected Lender"
                options={lenderOptions}
                SelectInputWrapper={StyledSelectWrapper}
                isLoading={loading}
                component={(props: JSX.IntrinsicAttributes & ISelectFieldProps) => (
                  <SelectAutocompleteField
                    canBeClean={false}
                    {...props}
                  />
                )}
                onChange={handleOnChange}
              />
            </LenderDropDown>
          </Grid>
          <Grid
            item
            md={3}
          >
            <Field
              name="financeConditionItem.rate"
              label="Rate (%)"
              suffix="%"
              decimalScale={2}
              defaultValue={0.0}
              InputWrapper={StyledNumberInputWrapper}
              component={NumberInputField}
              disabled={!canEditRateAndTerm}
              validateOnBlur={true}
              onBlur={handleOnBlur}
              onKeyDown={handleRateKeyDown}
            />
          </Grid>
          <Grid
            item
            md={3}
          >
            <Field
              name="financeConditionItem.term"
              label="Term (mo)"
              defaultValue={0}
              InputWrapper={StyledNumberInputWrapper}
              component={NumberInputField}
              disabled={!canEditRateAndTerm}
              validateOnBlur={true}
              onBlur={handleOnTermChange}
              onKeyDown={handleTermKeyDown}
            />
          </Grid>
          {lenderUtility?.submitNakedDeal && <Typography variant='subtitle2' color='error' sx={{ ml: 2, mb: -2.5, fontStyle: 'oblique' }}>Submit without product</Typography>}
        </Grid>
      </LenderWrapper>
    </>
  );
};
